import React, { memo } from 'react';
import { cn } from '@/lib/utils';
import { 
  MessageSquare, 
  Inbox, 
  Plus, 
  Zap,
  ArrowRight,
  Check,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// ============================================================================
// EMPTY STATE COMPONENT
// ============================================================================

export const InboxEmptyState = memo(({
  type = 'no-conversations', // 'no-conversations' | 'no-results' | 'all-done'
  hasConnectedChannels = false,
  searchQuery = '',
  onConnectChannel,
  onClearFilters,
  className = ''
}) => {
  // No conversations at all
  if (type === 'no-conversations') {
    return (
      <div className={cn(
        'flex flex-col items-center justify-center py-16 px-6',
        'bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800',
        className
      )}>
        {/* Illustration */}
        <div className="relative mb-6">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30 flex items-center justify-center">
            <Inbox className="w-12 h-12 text-blue-500" />
          </div>
          <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-green-500" />
          </div>
        </div>

        {/* Content */}
        <h3 className="text-xl font-bold text-slate-900 dark:text-zinc-50 mb-2 text-center">
          {hasConnectedChannels ? 'Menunggu Pesan' : 'Belum Ada Percakapan'}
        </h3>
        
        <p className="text-sm text-slate-500 dark:text-zinc-400 text-center max-w-md mb-6">
          {hasConnectedChannels 
            ? 'Channel sudah terhubung. Pesan dari customer akan muncul di sini secara otomatis.'
            : 'Hubungkan channel komunikasi seperti WhatsApp, Messenger, atau Instagram untuk mulai menerima pesan customer.'}
        </p>

        {/* Action */}
        {!hasConnectedChannels && (
          <Button 
            onClick={onConnectChannel}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Hubungkan Channel
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}

        {hasConnectedChannels && (
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
            <Zap className="w-5 h-5" />
            <span className="font-medium">Channel Connected - Ready to Receive</span>
          </div>
        )}

        {/* Quick Setup Checklist */}
        {!hasConnectedChannels && (
          <div className="mt-8 w-full max-w-sm">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 text-center dark:text-zinc-400">Quick Setup</p>
            <div className="space-y-2">
              {[
                { label: 'Hubungkan WhatsApp Business', done: false },
                { label: 'Setup AI Agent', done: false },
                { label: 'Tambahkan Katalog Produk', done: false }
              ].map((item, idx) => (
                <div 
                  key={idx}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg',
                    'bg-slate-50 dark:bg-zinc-800/50',
                    'text-sm text-slate-600 dark:text-zinc-300'
                  )}
                >
                  <div className={cn(
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                    item.done 
                      ? 'bg-green-500 border-green-500' 
                      : 'border-slate-300 dark:border-zinc-700'
                  )}>
                    {item.done && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // No search results
  if (type === 'no-results') {
    return (
      <div className={cn(
        'flex flex-col items-center justify-center py-12 px-6',
        'bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800',
        className
      )}>
        <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
          <MessageSquare className="w-8 h-8 text-slate-500 dark:text-zinc-400" />
        </div>
        
        <h3 className="text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-2">
          Tidak Ada Hasil
        </h3>
        
        <p className="text-sm text-slate-500 dark:text-zinc-400 text-center max-w-sm mb-4">
          {searchQuery 
            ? `Tidak ditemukan percakapan untuk "${searchQuery}"`
            : 'Tidak ada percakapan yang cocok dengan filter yang dipilih.'}
        </p>

        <Button 
          variant="outline"
          onClick={onClearFilters}
          className="text-sm"
        >
          Clear Filters
        </Button>
      </div>
    );
  }

  // All conversations done
  if (type === 'all-done') {
    return (
      <div className={cn(
        'flex flex-col items-center justify-center py-12 px-6',
        'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
        'rounded-xl border border-green-200 dark:border-green-800',
        className
      )}>
        <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-green-500" />
        </div>
        
        <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-2">
          Semua Selesai!
        </h3>
        
        <p className="text-sm text-green-600 dark:text-green-400 text-center max-w-sm">
          Tidak ada percakapan yang membutuhkan perhatian saat ini. Great job!
        </p>
      </div>
    );
  }

  return null;
});

InboxEmptyState.displayName = 'InboxEmptyState';

export default InboxEmptyState;
